import { useCallback } from 'react';
import { Shape } from '../types/Shape';
import { updateArrowPosition } from '../utils/arrowUtils';
import { calculateSnapPosition, AlignmentGuide } from '../utils/snapUtils';
import { useHistory } from './useHistory';

export interface UseShapeHandlersProps {
  shapes: Shape[];
  shapesRef: React.MutableRefObject<Shape[]>;
  selectedShapes: string[];
  stageScale: number;
  history: ReturnType<typeof useHistory>;
  hasActionStarted: React.MutableRefObject<boolean>;
  selectedShapesStartPositionsRef: React.MutableRefObject<Map<string, { x: number; y: number }>>;
  lastSnappedPositionRef: React.MutableRefObject<{ x: number | null; y: number | null } | null>;
  snapCalculationRafRef: React.MutableRefObject<number | null>;
  pendingSnapCalculationRef: React.MutableRefObject<{ shapeId: string; newX: number; newY: number } | null>;
  setDraggingShapeId: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedShapes: React.Dispatch<React.SetStateAction<string[]>>;
  setShowResizeHandles: React.Dispatch<React.SetStateAction<boolean>>;
  setAlignmentGuides: React.Dispatch<React.SetStateAction<AlignmentGuide[]>>;
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UseShapeHandlersReturn {
  handleDragPrepare: (shapeId: string) => void;
  handleDragStart: (shapeId: string) => void;
  handleDragMove: (shapeId: string, newX: number, newY: number) => void;
  handleDragEnd: (shapeId: string) => void;
  handleShapeResizeStart: (shapeId: string) => void;
  handleShapeResize: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  handleShapeResizeEnd: () => void;
  handleShapeClick: (shapeId: string, e: React.MouseEvent) => void;
}

export function useShapeHandlers({
  shapes,
  shapesRef,
  selectedShapes,
  stageScale,
  history,
  hasActionStarted,
  selectedShapesStartPositionsRef,
  lastSnappedPositionRef,
  snapCalculationRafRef,
  pendingSnapCalculationRef,
  setDraggingShapeId,
  setSelectedShapes,
  setShowResizeHandles,
  setAlignmentGuides,
  setShapes,
  setHasUnsavedChanges,
}: UseShapeHandlersProps): UseShapeHandlersReturn {

  const handleDragPrepare = useCallback((shapeId: string) => {
    setDraggingShapeId(shapeId);
  }, [setDraggingShapeId]);

  const handleDragStart = useCallback((shapeId: string) => {
    if (!hasActionStarted.current) {
      history.pushHistory(shapes);
      hasActionStarted.current = true;
    }
    if (!selectedShapes.includes(shapeId)) {
      setSelectedShapes([shapeId]);
    }
    selectedShapesStartPositionsRef.current.clear();
    const shapesToMove = selectedShapes.includes(shapeId) ? selectedShapes : [shapeId];

    shapesToMove.forEach(selectedId => {
      const selectedShape = shapes.find(s => s.id === selectedId);
      if (selectedShape) {
        selectedShapesStartPositionsRef.current.set(selectedId, {
          x: selectedShape.x,
          y: selectedShape.y
        });
      }
    });
    setShowResizeHandles(true);
    setAlignmentGuides([]);
  }, [shapes, selectedShapes, history, hasActionStarted, selectedShapesStartPositionsRef, setSelectedShapes, setShowResizeHandles, setAlignmentGuides]);

  const handleDragMove = useCallback((shapeId: string, newX: number, newY: number) => {
    const draggedShapeStartPos = selectedShapesStartPositionsRef.current.get(shapeId);
    if (!draggedShapeStartPos) {
      setShapes(prev => {
        const updatedShapes = prev.map(s =>
          s.id === shapeId ? { ...s, x: newX, y: newY } : s
        );

        const movedShape = updatedShapes.find(s => s.id === shapeId);
        if (movedShape && movedShape.connectedArrows) {
          movedShape.connectedArrows.forEach(arrowId => {
            const arrowIndex = updatedShapes.findIndex(s => s.id === arrowId);
            if (arrowIndex !== -1) {
              const arrow = updatedShapes[arrowIndex];
              if (arrow && arrow.type === 'arrow') {
                const updatedArrow = updateArrowPosition(arrow, movedShape, shapeId);
                updatedShapes[arrowIndex] = updatedArrow;
              }
            }
          });
        }

        return updatedShapes;
      });
      return;
    }

    const deltaX = newX - draggedShapeStartPos.x;
    const deltaY = newY - draggedShapeStartPos.y;

    const lastSnapped = lastSnappedPositionRef.current;
    let finalX = newX;
    let finalY = newY;
    let useDeadZone = false;

    if (lastSnapped) {
      const deadZone = 20 / stageScale;

      if (lastSnapped.x !== null) {
        const distanceX = Math.abs(newX - lastSnapped.x);
        if (distanceX < deadZone) {
          finalX = lastSnapped.x;
          useDeadZone = true;
        }
      }

      if (lastSnapped.y !== null) {
        const distanceY = Math.abs(newY - lastSnapped.y);
        if (distanceY < deadZone) {
          finalY = lastSnapped.y;
          useDeadZone = true;
        }
      }
    }

    if (useDeadZone) {
      const finalDeltaX = finalX - draggedShapeStartPos.x;
      const finalDeltaY = finalY - draggedShapeStartPos.y;

      setShapes(prev => {
        const updatedShapes = prev.map(s => {
          if (selectedShapesStartPositionsRef.current.has(s.id)) {
            const startPos = selectedShapesStartPositionsRef.current.get(s.id)!;
            return { ...s, x: startPos.x + finalDeltaX, y: startPos.y + finalDeltaY };
          }
          return s;
        });

        selectedShapes.forEach(selectedId => {
          const updatedDraggedShape = updatedShapes.find(s => s.id === selectedId);
          if (updatedDraggedShape && updatedDraggedShape.connectedArrows) {
            updatedDraggedShape.connectedArrows.forEach(arrowId => {
              const arrowIndex = updatedShapes.findIndex(s => s.id === arrowId);
              if (arrowIndex !== -1) {
                const arrow = updatedShapes[arrowIndex];
                if (arrow && arrow.type === 'arrow') {
                  const updatedArrow = updateArrowPosition(arrow, updatedDraggedShape, selectedId);
                  updatedShapes[arrowIndex] = updatedArrow;
                }
              }
            });
          }
        });

        return updatedShapes;
      });

      pendingSnapCalculationRef.current = null;
      return;
    }

    setShapes(prev => {
      const updatedShapes = prev.map(s => {
        if (selectedShapesStartPositionsRef.current.has(s.id)) {
          const startPos = selectedShapesStartPositionsRef.current.get(s.id)!;
          return { ...s, x: startPos.x + deltaX, y: startPos.y + deltaY };
        }
        return s;
      });

      selectedShapes.forEach(selectedId => {
        const updatedDraggedShape = updatedShapes.find(s => s.id === selectedId);
        if (updatedDraggedShape && updatedDraggedShape.connectedArrows) {
          updatedDraggedShape.connectedArrows.forEach(arrowId => {
            const arrowIndex = updatedShapes.findIndex(s => s.id === arrowId);
            if (arrowIndex !== -1) {
              const arrow = updatedShapes[arrowIndex];
              if (arrow && arrow.type === 'arrow') {
                const updatedArrow = updateArrowPosition(arrow, updatedDraggedShape, selectedId);
                updatedShapes[arrowIndex] = updatedArrow;
              }
            }
          });
        }
      });

      return updatedShapes;
    });

    pendingSnapCalculationRef.current = { shapeId, newX, newY };

    if (snapCalculationRafRef.current !== null) {
      cancelAnimationFrame(snapCalculationRafRef.current);
    }

    snapCalculationRafRef.current = requestAnimationFrame(() => {
      const pending = pendingSnapCalculationRef.current;
      if (pending) {
        setShapes(prev => {
          const currentShapes = prev;
          shapesRef.current = currentShapes;

          const draggedShape = currentShapes.find(s => s.id === pending.shapeId);
          if (draggedShape) {
            const snapThreshold = 12 / stageScale;

            const lastSnapped = lastSnappedPositionRef.current;
            const wasSnappedX = lastSnapped !== null && lastSnapped.x !== null &&
              Math.abs(draggedShape.x - lastSnapped.x) < 1;
            const wasSnappedY = lastSnapped !== null && lastSnapped.y !== null &&
              Math.abs(draggedShape.y - lastSnapped.y) < 1;
            const wasSnapped = wasSnappedX || wasSnappedY;

            const releaseThreshold = wasSnapped ? 15 / stageScale : snapThreshold;

            if (lastSnapped) {
              let shouldClearX = false;
              let shouldClearY = false;

              if (lastSnapped.x !== null) {
                const distanceX = Math.abs(pending.newX - lastSnapped.x);
                if (distanceX > releaseThreshold) {
                  shouldClearX = true;
                }
              }

              if (lastSnapped.y !== null) {
                const distanceY = Math.abs(pending.newY - lastSnapped.y);
                if (distanceY > releaseThreshold) {
                  shouldClearY = true;
                }
              }

              if (shouldClearX || shouldClearY) {
                if (shouldClearX && shouldClearY) {
                  lastSnappedPositionRef.current = null;
                } else {
                  lastSnappedPositionRef.current = {
                    x: shouldClearX ? null : lastSnapped.x,
                    y: shouldClearY ? null : lastSnapped.y,
                  };
                }
              } else if (wasSnappedX && wasSnappedY) {
                setAlignmentGuides([]);
                return prev;
              }
            }

            const snapResult = calculateSnapPosition(
              draggedShape,
              pending.newX,
              pending.newY,
              currentShapes,
              snapThreshold
            );

            const snapDistanceX = Math.abs(snapResult.snappedX - pending.newX);
            const snapDistanceY = Math.abs(snapResult.snappedY - pending.newY);

            let newSnappedX: number | null = null;
            let newSnappedY: number | null = null;

            if (snapDistanceX > 2) {
              if (!wasSnappedX) {
                newSnappedX = snapResult.snappedX;
              } else if (snapDistanceX > 5) {
                newSnappedX = snapResult.snappedX;
              } else {
                newSnappedX = lastSnapped?.x ?? null;
              }
            } else {
              newSnappedX = null;
            }

            if (snapDistanceY > 2) {
              if (!wasSnappedY) {
                newSnappedY = snapResult.snappedY;
              } else if (snapDistanceY > 5) {
                newSnappedY = snapResult.snappedY;
              } else {
                newSnappedY = lastSnapped?.y ?? null;
              }
            } else {
              newSnappedY = null;
            }

            if (newSnappedX !== null || newSnappedY !== null || snapDistanceX > 2 || snapDistanceY > 2) {
              const finalX = newSnappedX !== null ? newSnappedX : pending.newX;
              const finalY = newSnappedY !== null ? newSnappedY : pending.newY;

              if (newSnappedX !== null || newSnappedY !== null) {
                lastSnappedPositionRef.current = {
                  x: newSnappedX !== null ? newSnappedX : lastSnapped?.x ?? null,
                  y: newSnappedY !== null ? newSnappedY : lastSnapped?.y ?? null,
                };

                setAlignmentGuides(snapResult.guides);

                const draggedShapeStartPos = selectedShapesStartPositionsRef.current.get(pending.shapeId);
                if (draggedShapeStartPos) {
                  const deltaX = finalX - draggedShapeStartPos.x;
                  const deltaY = finalY - draggedShapeStartPos.y;

                  let updatedShapes = prev.map(s => {
                    if (selectedShapesStartPositionsRef.current.has(s.id)) {
                      const startPos = selectedShapesStartPositionsRef.current.get(s.id)!;
                      return { ...s, x: startPos.x + deltaX, y: startPos.y + deltaY };
                    }
                    return s;
                  });

                  selectedShapes.forEach(selectedId => {
                    const updatedDraggedShape = updatedShapes.find(s => s.id === selectedId);
                    if (updatedDraggedShape && updatedDraggedShape.connectedArrows) {
                      updatedDraggedShape.connectedArrows.forEach(arrowId => {
                        const arrowIndex = updatedShapes.findIndex(s => s.id === arrowId);
                        if (arrowIndex !== -1) {
                          const arrow = updatedShapes[arrowIndex];
                          if (arrow && arrow.type === 'arrow') {
                            const updatedArrow = updateArrowPosition(arrow, updatedDraggedShape, selectedId);
                            updatedShapes[arrowIndex] = updatedArrow;
                          }
                        }
                      });
                    }
                  });

                  return updatedShapes;
                }
              } else {
                setAlignmentGuides([]);
              }
            } else {
              setAlignmentGuides([]);
            }
          }

          return prev;
        });
      }
      pendingSnapCalculationRef.current = null;
      snapCalculationRafRef.current = null;
    });
  }, [shapes, selectedShapes, stageScale, shapesRef, selectedShapesStartPositionsRef, lastSnappedPositionRef, snapCalculationRafRef, pendingSnapCalculationRef, setShapes, setAlignmentGuides]);

  const handleDragEnd = useCallback((shapeId: string) => {
    // 드래그 종료
    setDraggingShapeId(null);
    hasActionStarted.current = false;
    setAlignmentGuides([]);
    setHasUnsavedChanges(true);

    // 다중 선택 객체들의 초기 위치 초기화
    selectedShapesStartPositionsRef.current.clear();

    // pending 스냅 계산 취소
    if (snapCalculationRafRef.current !== null) {
      cancelAnimationFrame(snapCalculationRafRef.current);
      snapCalculationRafRef.current = null;
    }
    pendingSnapCalculationRef.current = null;
    lastSnappedPositionRef.current = null;
  }, [setDraggingShapeId, hasActionStarted, selectedShapesStartPositionsRef, snapCalculationRafRef, pendingSnapCalculationRef, lastSnappedPositionRef, setAlignmentGuides, setHasUnsavedChanges]);

  const handleShapeResizeStart = useCallback((shapeId: string) => {
    // 리사이즈 시작 시 현재 상태를 히스토리에 저장
    if (!hasActionStarted.current) {
      history.pushHistory(shapes);
      hasActionStarted.current = true;
    }
  }, [shapes, history, hasActionStarted]);

  const handleShapeResize = useCallback((shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => {
    setShapes(prev => {
      const updatedShapes = prev.map(s =>
        s.id === shapeId
          ? { ...s, width: newWidth, height: newHeight, x: newX ?? s.x, y: newY ?? s.y }
          : s
      );

      // 연결된 화살표 업데이트
      const resizedShape = updatedShapes.find(s => s.id === shapeId);
      if (resizedShape && resizedShape.connectedArrows) {
        resizedShape.connectedArrows.forEach(arrowId => {
          const arrowIndex = updatedShapes.findIndex(s => s.id === arrowId);
          if (arrowIndex !== -1) {
            const arrow = updatedShapes[arrowIndex];
            if (arrow && arrow.type === 'arrow') {
              const updatedArrow = updateArrowPosition(arrow, resizedShape, shapeId);
              updatedShapes[arrowIndex] = updatedArrow;
            }
          }
        });
      }

      return updatedShapes;
    });
  }, [setShapes]);

  const handleShapeResizeEnd = useCallback(() => {
    hasActionStarted.current = false;
    setShowResizeHandles(true);
    setHasUnsavedChanges(true);
  }, [hasActionStarted, setShowResizeHandles, setHasUnsavedChanges]);

  const handleShapeClick = useCallback((shapeId: string, e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      // 다중 선택
      setSelectedShapes(prev =>
        prev.includes(shapeId)
          ? prev.filter(id => id !== shapeId)
          : [...prev, shapeId]
      );
    } else {
      setSelectedShapes([shapeId]);
    }
    setShowResizeHandles(true);
  }, [setSelectedShapes, setShowResizeHandles]);

  return {
    handleDragPrepare,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleShapeResizeStart,
    handleShapeResize,
    handleShapeResizeEnd,
    handleShapeClick,
  };
}
